import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { useRealtimeNotifications } from "@/hooks/useRealtimeNotifications";
import { usePresenceTracker } from "@/hooks/useOnlineStatus";
import ProtectedRoute from "@/components/ProtectedRoute";
import { NotificationPermissionModal } from "@/components/NotificationPermissionModal";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

// Pages
import AuthPage from "@/pages/AuthPage";
import ProfileSetupPage from "@/pages/ProfileSetupPage";
import ChatPage from "@/pages/ChatPage";
import ConversationsListPage from "@/pages/ConversationsListPage";
import NewConversationPage from "@/pages/NewConversationPage";
import ServicesPage from "@/pages/ServicesPage";
import ProfilePage from "@/pages/ProfilePage";
import AgentInboxPage from "@/pages/AgentInboxPage";
import AdminPage from "@/pages/AdminPage";
import InstallPage from "@/pages/InstallPage";
import NotFound from "@/pages/NotFound";

const queryClient = new QueryClient();

function AppRoutes() {
  const { user, loading, isAgent } = useAuth();
  
  // Enable realtime notifications
  useRealtimeNotifications();
  
  // Track user online presence
  usePresenceTracker();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* PWA install prompt - shows if not installed */}
      <PWAInstallPrompt />
      
      {/* Notification permission modal - shows after login */}
      <NotificationPermissionModal />
      
      <Routes>
        {/* Public routes */}
      <Route
        path="/auth"
        element={user ? <Navigate to="/" replace /> : <AuthPage />}
      />
      <Route path="/install" element={<InstallPage />} />

      {/* User routes */}
      <Route
        path="/"
        element={
          user ? (
            isAgent ? (
              <Navigate to="/agent" replace />
            ) : (
              <Navigate to="/services" replace />
            )
          ) : (
            <Navigate to="/auth" replace />
          )
        }
      />
      <Route
        path="/profile/setup"
        element={
          <ProtectedRoute>
            <ProfileSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ConversationsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/new"
        element={
          <ProtectedRoute>
            <NewConversationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:conversationId"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute>
            <ServicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Agent routes */}
      <Route
        path="/agent"
        element={
          <ProtectedRoute requireAgent>
            <AgentInboxPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminPage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <NotificationsProvider>
            <AppRoutes />
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
